import { TypedEventEmitter } from "../lib/core";
import { GridSystem, ItemSystem, MergeSystem, ShadeSystem, SpawnSystem, UISystem } from "./systems";
import type {
  CompleteGameData,
  ItemState,
  instanceId,
  Position,
  tileKey,
  TileState,
} from "../data/types";
import { markRaw, reactive } from "vue";
import { FakeImplements } from "../lib/util";
import type { GameEvents, SerializedEvents } from "./events";

type highlightType =
  | "hover" // hovering an empty tile
  | "drop" // dragging an item — this tile is the landing spot
  | "match" // item matches the cursor item
  | "merge" // item can be merged with the cursor item on drop
  | "spawn" // preview of where this item will spawn drops on tap/charge
  | "swap" // item will be swapped on drop
  | "cover" // in explosion range, no shade
  | "cover-damage" // in explosion range, has shade, survives
  | "cover-clear"; // in explosion range, has shade, gets cleared

interface CompleteGameState {
  tick: number;
  items: Map<instanceId, Item>;
  tiles: Map<tileKey, Tile>;
  size: Position;
  dragging: {
    from: Position;
    over: Position | null;
    itemUid: instanceId;
  } | null;
  highlight: Record<
    tileKey,
    {
      /** highlight of the tile itself */
      tile: highlightType | null;
      /** highlight of the tile item. for dragged item it's the from tile */
      item: highlightType | null;
    }
  >;
  scheduledEvents: {
    event: keyof SerializedEvents;
    atTick: number;
    detail: SerializedEvents[keyof SerializedEvents];
  }[];
  /** Max orb power achieved - determines visibility into shade */
  visibilityThreshold: number;
}

export type SerializedGameState = Omit<CompleteGameState, "items" | "tiles"> & {
  items: Record<string, unknown>;
  tiles: Record<string, unknown>;
};

export class Item extends FakeImplements<ItemState>()(Object) {
  constructor(itemData: ItemState) {
    super();
    const { data, ...rest } = itemData;
    Object.assign(this, rest);
    // Make data non-enumerable so it won't be serialized
    Object.defineProperty(this, "data", {
      value: data,
      writable: true,
      enumerable: false,
      configurable: true,
    });
    return reactive(this);
  }

  static from(saved: any, game: Game): Item {
    const data = game.data.items[saved.id];
    if (!data) throw new Error(`Item data not found for id: ${saved.id}`);
    return new Item({ ...saved, data });
  }
}

export class Tile {
  position!: Position;
  key!: tileKey;
  itemUid!: instanceId | null;
  shade: number = 0; // shade density 0-100+
  declare game: Game;

  constructor(
    data: TileState,
    game: Game,
  ) {
    Object.defineProperty(this, "game", { get: () => game, set: () => {}, configurable: true });
    // Make position non-enumerable so it won't be serialized
    Object.defineProperty(this, "position", {
      value: data.position,
      writable: true,
      enumerable: false,
      configurable: true,
    });
    // Set key as enumerable property (will be serialized)
    this.key = Tile.key(data.position);
    // Assign rest of properties
    const { position, ...rest } = data;
    Object.assign(this, rest);
    return reactive(this) as any;
  }

  static from(saved: any, game: Game): Tile {
    // Parse position from key
    const [x, y] = saved.key.split(",").map(Number);
    const position = { x, y };
    return new Tile({ position, itemUid: saved.itemUid, shade: saved.shade }, game);
  }

  static key({ x, y }: Position): tileKey {
    return `${x},${y}` as tileKey;
  }

  static position(key: tileKey): Position {
    const [x, y] = key.split(",").map(Number);
    return { x, y };
  }

  get item(): Item | null {
    if (!this.itemUid) return null;
    const item = this.game.state.items.get(this.itemUid);
    if (!item) {
      // Item was deleted but tile still has stale UID - clean it up
      this.itemUid = null;
      return null;
    }
    return item;
  }

  set item(value: Item | null) {
    this.itemUid = value?.uid ?? null;
  }

}

export class Game extends TypedEventEmitter<GameEvents, SerializedEvents> {
  data: CompleteGameData;
  state: CompleteGameState;
  _lastUid = 0;

  declare grid: GridSystem;
  declare item: ItemSystem;
  declare spawn: SpawnSystem;
  declare ui: UISystem;
  declare merge: MergeSystem;
  declare shade: ShadeSystem;

  override get scheduledEvents(): {
    event: keyof SerializedEvents;
    atTick: number;
    detail: SerializedEvents[keyof SerializedEvents];
  }[] {
    return this.state.scheduledEvents;
  }

  constructor(data: CompleteGameData, state: CompleteGameState) {
    super();
    this.data = data;
    this.state = reactive(state);
    this.installSystem("grid", new GridSystem(this));
    this.installSystem("item", new ItemSystem(this));
    this.installSystem("spawn", new SpawnSystem(this));
    this.installSystem("ui", new UISystem(this));
    this.installSystem("merge", new MergeSystem(this));
    this.installSystem("shade", new ShadeSystem(this));
    this.registerEventHandlers();
    markRaw(this);
  }

  tick(): void {
    // Increment tick and emit event (handler will update state and process scheduled events)
    const tick = this.state.tick + 1;
    this.emit("tick", { tick });
  }

  /** Serialize game state to JSON-compatible object */
  saveState(): SerializedGameState {
    let state = {
      ...this.state,
      dragging: null,
      items: Object.fromEntries(this.state.items),
      tiles: Object.fromEntries(this.state.tiles),
    };
    state = JSON.parse(JSON.stringify(state));
    return state;
  }

  /** Load game state from serialized data */
  loadState(saved: SerializedGameState) {
    Object.assign(this.state, {
      ...saved,
      dragging: null,
      items: new Map(
        Object.entries(saved.items).map(([uid, data]) => {
          const item = Item.from(data, this);
          return [item.uid, item];
        }),
      ),
      tiles: new Map(
        Object.entries(saved.tiles).map(([key, data]) => {
          const tile = Tile.from(data, this);
          return [tile.key, tile];
        }),
      ),
    });
    this.merge.settleBoard();
  }
}
